import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { getFirebaseFirestore } from '@/services/firebase/config';
import { doc, getDoc, setDoc } from 'firebase/firestore';

const db = getFirebaseFirestore();
import {
  Building2,
  DollarSign,
  Package,
  Target,
  Settings,
  Megaphone,
  CheckCircle2,
  AlertCircle,
  Brain,
  Sparkles,
  ChevronRight,
  TrendingUp,
  Percent
} from 'lucide-react';
import {
  CompanyProfile,
  ProfileSection,
  CompanyInfo,
  FinancialInfo,
  ProductStockInfo,
  BusinessGoals,
  OperationalInfo,
  MarketingInfo
} from '../types/companyProfile';
import CompanyInfoModal from './profile-modals/CompanyInfoModal';
import FinancialInfoModal from './profile-modals/FinancialInfoModal';
import ProductStockInfoModal from './profile-modals/ProductStockInfoModal';
import BusinessGoalsModal from './profile-modals/BusinessGoalsModal';
import OperationalInfoModal from './profile-modals/OperationalInfoModal';
import MarketingInfoModal from './profile-modals/MarketingInfoModal';

interface SectionConfig {
  id: ProfileSection;
  title: string;
  description: string;
  icon: React.ElementType;
  color: string;
  bgColor: string;
  borderColor: string;
  fields: string[];
}

const SECTIONS: SectionConfig[] = [
  {
    id: 'companyInfo',
    title: 'Şirket Bilgileri',
    description: 'Şirketinizin temel bilgileri, kuruluş tarihi, sektör ve lokasyon',
    icon: Building2,
    color: 'text-blue-400',
    bgColor: 'bg-blue-500/10',
    borderColor: 'border-blue-500/30',
    fields: ['foundedDate', 'companyType', 'employeeCount', 'sector', 'targetAudience', 'country', 'city', 'address', 'warehouseLocations']
  },
  {
    id: 'financialInfo',
    title: 'Finansal Bilgiler',
    description: 'Aylık ciro, hedef kar marjı ve reklam bütçesi dağılımı',
    icon: DollarSign,
    color: 'text-green-400',
    bgColor: 'bg-green-500/10',
    borderColor: 'border-green-500/30',
    fields: ['monthlyRevenue', 'revenueCurrency', 'targetProfitMargin', 'adBudget']
  },
  {
    id: 'productStockInfo',
    title: 'Ürün & Stok Bilgileri',
    description: 'Üretim tipi, tedarik süresi ve minimum stok politikası',
    icon: Package,
    color: 'text-purple-400',
    bgColor: 'bg-purple-500/10',
    borderColor: 'border-purple-500/30',
    fields: ['productionType', 'averageLeadTime', 'minStockPolicy']
  },
  {
    id: 'businessGoals',
    title: 'İş Hedefleri',
    description: 'Yıllık büyüme hedefi, rakipler ve SWOT analizi',
    icon: Target,
    color: 'text-orange-400',
    bgColor: 'bg-orange-500/10',
    borderColor: 'border-orange-500/30',
    fields: ['yearlyGrowthTarget', 'expansionPlans', 'competitors', 'strengths', 'weaknesses']
  },
  {
    id: 'operationalInfo',
    title: 'Operasyonel Bilgiler',
    description: 'Kargo firmaları, müşteri hizmetleri ve sezonsal dönemler',
    icon: Settings,
    color: 'text-cyan-400',
    bgColor: 'bg-cyan-500/10',
    borderColor: 'border-cyan-500/30',
    fields: ['cargoCompanies', 'customerServiceHours', 'seasonalPeriods']
  },
  {
    id: 'marketingInfo',
    title: 'Pazarlama Bilgileri',
    description: 'Marka bilinirliği, sosyal medya ve pazarlama kanalları',
    icon: Megaphone,
    color: 'text-pink-400',
    bgColor: 'bg-pink-500/10',
    borderColor: 'border-pink-500/30',
    fields: ['brandAwareness', 'socialMedia', 'emailMarketing', 'influencerMarketing']
  }
];

const AI_MESSAGES = [
  'Yapay Zeka öğreniyor...',
  'AI analiz yapıyor...',
  'Veriler işleniyor...',
  'Model güncelleniyor...',
  'Öneriler hazırlanıyor...'
];

const CompanyProfilePage: React.FC = () => {
  const { user } = useAuth();
  const [profile, setProfile] = useState<CompanyProfile>({});
  const [activeModal, setActiveModal] = useState<ProfileSection | null>(null);
  const [aiMessageIndex, setAiMessageIndex] = useState(0);
  const [isAiAnimating, setIsAiAnimating] = useState(false);

  // AI message rotation
  useEffect(() => {
    if (isAiAnimating) {
      const interval = setInterval(() => {
        setAiMessageIndex(prev => (prev + 1) % AI_MESSAGES.length);
      }, 2000);
      return () => clearInterval(interval);
    }
  }, [isAiAnimating]);

  // Profili Firestore'dan yükle (önce localStorage'dan migrate et)
  const loadProfile = useCallback(async () => {
    if (!user?.uid) {
      // Kullanıcı giriş yapmamışsa localStorage'dan oku
      const savedProfile = localStorage.getItem('companyProfile');
      if (savedProfile) setProfile(JSON.parse(savedProfile));
      return;
    }

    try {
      const docRef = doc(db, 'users', user.uid, 'ai_profile', 'data');
      const snap = await getDoc(docRef);

      if (snap.exists()) {
        setProfile(snap.data() as CompanyProfile);
      } else {
        // Firestore'da yoksa localStorage'dan migrate et
        const savedProfile = localStorage.getItem('companyProfile');
        if (savedProfile) {
          const parsed = JSON.parse(savedProfile) as CompanyProfile;
          setProfile(parsed);
          // Tek seferlik migrate
          await setDoc(docRef, { ...parsed, migratedAt: new Date() }, { merge: true });
          localStorage.removeItem('companyProfile');
        }
      }
    } catch (err) {
      console.error('Profil yüklenemedi:', err);
      const savedProfile = localStorage.getItem('companyProfile');
      if (savedProfile) setProfile(JSON.parse(savedProfile));
    }
  }, [user?.uid]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  // Profili Firestore'a kaydet
  const saveProfile = async (newProfile: CompanyProfile) => {
    const updatedProfile = {
      ...newProfile,
      updatedAt: new Date(),
      completionPercentage: calculateCompletion(newProfile)
    };
    setProfile(updatedProfile);

    if (user?.uid) {
      try {
        const docRef = doc(db, 'users', user.uid, 'ai_profile', 'data');
        await setDoc(docRef, updatedProfile, { merge: true });
      } catch (err) {
        console.error('Profil kaydedilemedi, localStorage\'a yazıldı:', err);
        localStorage.setItem('companyProfile', JSON.stringify(updatedProfile));
      }
    } else {
      localStorage.setItem('companyProfile', JSON.stringify(updatedProfile));
    }

    // Trigger AI animation
    setIsAiAnimating(true);
    setTimeout(() => setIsAiAnimating(false), 6000);
  };

  // Calculate section completion
  const getSectionCompletion = (sectionId: ProfileSection): { filled: number; total: number; percentage: number } => {
    const section = SECTIONS.find(s => s.id === sectionId);
    if (!section) return { filled: 0, total: 0, percentage: 0 };

    const sectionData = profile[sectionId];
    if (!sectionData) return { filled: 0, total: section.fields.length, percentage: 0 };

    let filledCount = 0;
    section.fields.forEach(field => {
      const value = (sectionData as Record<string, unknown>)[field];
      if (value !== undefined && value !== null && value !== '') {
        if (Array.isArray(value)) {
          if (value.length > 0) filledCount++;
        } else if (typeof value === 'object') {
          if (Object.keys(value).some(k => (value as Record<string, unknown>)[k])) filledCount++;
        } else {
          filledCount++;
        }
      }
    });

    return {
      filled: filledCount,
      total: section.fields.length,
      percentage: Math.round((filledCount / section.fields.length) * 100)
    };
  };

  // Calculate total completion
  const calculateCompletion = (profileData: CompanyProfile): number => {
    let totalFilled = 0;
    let totalFields = 0;

    SECTIONS.forEach(section => {
      const sectionData = profileData[section.id];
      totalFields += section.fields.length;

      if (sectionData) {
        section.fields.forEach(field => {
          const value = (sectionData as Record<string, unknown>)[field];
          if (value !== undefined && value !== null && value !== '') {
            if (Array.isArray(value)) {
              if (value.length > 0) totalFilled++;
            } else if (typeof value === 'object') {
              if (Object.keys(value).some(k => (value as Record<string, unknown>)[k])) totalFilled++;
            } else {
              totalFilled++;
            }
          }
        });
      }
    });

    return Math.round((totalFilled / totalFields) * 100);
  };

  const totalCompletion = calculateCompletion(profile);

  // Modal handlers
  const handleSaveCompanyInfo = (data: CompanyInfo) => {
    saveProfile({ ...profile, companyInfo: data });
    setActiveModal(null);
  };

  const handleSaveFinancialInfo = (data: FinancialInfo) => {
    saveProfile({ ...profile, financialInfo: data });
    setActiveModal(null);
  };

  const handleSaveProductStockInfo = (data: ProductStockInfo) => {
    saveProfile({ ...profile, productStockInfo: data });
    setActiveModal(null);
  };

  const handleSaveBusinessGoals = (data: BusinessGoals) => {
    saveProfile({ ...profile, businessGoals: data });
    setActiveModal(null);
  };

  const handleSaveOperationalInfo = (data: OperationalInfo) => {
    saveProfile({ ...profile, operationalInfo: data });
    setActiveModal(null);
  };

  const handleSaveMarketingInfo = (data: MarketingInfo) => {
    saveProfile({ ...profile, marketingInfo: data });
    setActiveModal(null);
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <Brain className="w-7 h-7 text-purple-400" />
            AI Eğitim Profili
          </h1>
          <p className="text-gray-400 mt-1">
            Yapay zekanın size daha iyi öneriler sunabilmesi için işletmenizi tanıtın
          </p>
        </div>

        {/* AI Status */}
        <div className={`flex items-center gap-3 px-4 py-2 rounded-xl transition-all duration-500 ${
          isAiAnimating
            ? 'bg-purple-500/20 border border-purple-500/50'
            : 'bg-[#1a1a2e]/50 border border-white/5'
        }`}>
          <div className={`relative ${isAiAnimating ? 'animate-pulse' : ''}`}>
            <Sparkles className={`w-5 h-5 ${isAiAnimating ? 'text-purple-400' : 'text-gray-500'}`} />
            {isAiAnimating && (
              <div className="absolute inset-0 animate-ping">
                <Sparkles className="w-5 h-5 text-purple-400 opacity-50" />
              </div>
            )}
          </div>
          <span className={`text-sm font-medium transition-colors duration-300 ${
            isAiAnimating ? 'text-purple-300' : 'text-gray-400'
          }`}>
            {isAiAnimating ? AI_MESSAGES[aiMessageIndex] : 'AI hazır'}
          </span>
        </div>
      </div>

      {/* Overall Progress Card */}
      <div className="bg-gradient-to-r from-purple-500/10 via-blue-500/10 to-cyan-500/10 rounded-2xl p-6 border border-white/10">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">Profil Tamamlanma Oranı</h3>
              <p className="text-sm text-gray-400">
                {totalCompletion === 100
                  ? 'Tebrikler! Profiliniz tamamlandı.'
                  : 'Daha fazla bilgi, daha iyi AI önerileri'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Percent className="w-5 h-5 text-purple-400" />
            <span className="text-3xl font-bold text-white">{totalCompletion}</span>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="h-3 bg-white/5 rounded-full overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-1000 ease-out bg-gradient-to-r from-purple-500 via-blue-500 to-cyan-500"
            style={{ width: `${totalCompletion}%` }}
          />
        </div>

        {/* Section mini indicators */}
        <div className="flex justify-between mt-4">
          {SECTIONS.map((section) => {
            const completion = getSectionCompletion(section.id);
            const Icon = section.icon;
            return (
              <div
                key={section.id}
                className="flex flex-col items-center gap-1"
              >
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                  completion.percentage === 100
                    ? 'bg-green-500/20'
                    : completion.percentage > 0
                      ? 'bg-yellow-500/20'
                      : 'bg-white/5'
                }`}>
                  <Icon className={`w-4 h-4 ${
                    completion.percentage === 100
                      ? 'text-green-400'
                      : completion.percentage > 0
                        ? 'text-yellow-400'
                        : 'text-gray-500'
                  }`} />
                </div>
                <span className="text-xs text-gray-500">{completion.percentage}%</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Section Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {SECTIONS.map((section) => {
          const completion = getSectionCompletion(section.id);
          const isComplete = completion.percentage === 100;
          const hasData = completion.percentage > 0;
          const Icon = section.icon;

          return (
            <button
              key={section.id}
              onClick={() => setActiveModal(section.id)}
              className={`group relative p-5 rounded-xl text-left transition-all duration-300 hover:scale-[1.02] ${
                isComplete
                  ? 'bg-green-500/10 border-2 border-green-500/30 hover:border-green-500/50'
                  : hasData
                    ? 'bg-yellow-500/5 border-2 border-yellow-500/20 hover:border-yellow-500/40'
                    : 'bg-[#1a1a2e]/50 border-2 border-red-500/20 hover:border-red-500/40'
              }`}
            >
              {/* Status Badge */}
              <div className={`absolute top-3 right-3 flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium ${
                isComplete
                  ? 'bg-green-500/20 text-green-400'
                  : hasData
                    ? 'bg-yellow-500/20 text-yellow-400'
                    : 'bg-red-500/20 text-red-400'
              }`}>
                {isComplete ? (
                  <>
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    Tamamlandı
                  </>
                ) : hasData ? (
                  <>
                    <AlertCircle className="w-3.5 h-3.5" />
                    {completion.percentage}%
                  </>
                ) : (
                  <>
                    <AlertCircle className="w-3.5 h-3.5" />
                    Eksik
                  </>
                )}
              </div>

              {/* Icon */}
              <div className={`w-12 h-12 rounded-xl ${section.bgColor} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                <Icon className={`w-6 h-6 ${section.color}`} />
              </div>

              {/* Content */}
              <h3 className="text-lg font-semibold text-white mb-1 group-hover:text-purple-300 transition-colors">
                {section.title}
              </h3>
              <p className="text-sm text-gray-400 mb-4 line-clamp-2">
                {section.description}
              </p>

              {/* Progress */}
              <div className="space-y-2">
                <div className="flex justify-between text-xs">
                  <span className="text-gray-500">{completion.filled} / {completion.total} alan</span>
                  <span className={isComplete ? 'text-green-400' : hasData ? 'text-yellow-400' : 'text-red-400'}>
                    {completion.percentage}%
                  </span>
                </div>
                <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-500 ${
                      isComplete
                        ? 'bg-green-500'
                        : hasData
                          ? 'bg-yellow-500'
                          : 'bg-red-500/50'
                    }`}
                    style={{ width: `${completion.percentage}%` }}
                  />
                </div>
              </div>

              {/* Arrow */}
              <div className="absolute bottom-5 right-5 opacity-0 group-hover:opacity-100 transition-opacity">
                <ChevronRight className="w-5 h-5 text-purple-400" />
              </div>
            </button>
          );
        })}
      </div>

      {/* AI Tips */}
      <div className="bg-[#1a1a2e]/50 rounded-xl p-4 border border-white/5">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center flex-shrink-0">
            <Brain className="w-5 h-5 text-purple-400" />
          </div>
          <div>
            <h4 className="text-sm font-medium text-white mb-1">AI İpucu</h4>
            <p className="text-sm text-gray-400">
              {totalCompletion < 30 && 'Profilinizi doldurmaya başlayın! Her bilgi, AI\'ın size daha iyi öneriler sunmasına yardımcı olur.'}
              {totalCompletion >= 30 && totalCompletion < 60 && 'Harika gidiyorsunuz! Finansal ve hedef bilgilerinizi ekleyerek AI\'ın analiz kalitesini artırabilirsiniz.'}
              {totalCompletion >= 60 && totalCompletion < 100 && 'Neredeyse tamam! Son birkaç alanı doldurun ve AI\'dan maksimum fayda alın.'}
              {totalCompletion === 100 && 'Mükemmel! Profiliniz tamamlandı. AI artık size en iyi önerileri sunabilir.'}
            </p>
          </div>
        </div>
      </div>

      {/* Modals */}
      {activeModal === 'companyInfo' && (
        <CompanyInfoModal
          isOpen={true}
          onClose={() => setActiveModal(null)}
          initialData={profile.companyInfo}
          onSave={handleSaveCompanyInfo}
        />
      )}
      {activeModal === 'financialInfo' && (
        <FinancialInfoModal
          isOpen={true}
          onClose={() => setActiveModal(null)}
          initialData={profile.financialInfo}
          onSave={handleSaveFinancialInfo}
        />
      )}
      {activeModal === 'productStockInfo' && (
        <ProductStockInfoModal
          isOpen={true}
          onClose={() => setActiveModal(null)}
          initialData={profile.productStockInfo}
          onSave={handleSaveProductStockInfo}
        />
      )}
      {activeModal === 'businessGoals' && (
        <BusinessGoalsModal
          isOpen={true}
          onClose={() => setActiveModal(null)}
          initialData={profile.businessGoals}
          onSave={handleSaveBusinessGoals}
        />
      )}
      {activeModal === 'operationalInfo' && (
        <OperationalInfoModal
          isOpen={true}
          onClose={() => setActiveModal(null)}
          initialData={profile.operationalInfo}
          onSave={handleSaveOperationalInfo}
        />
      )}
      {activeModal === 'marketingInfo' && (
        <MarketingInfoModal
          isOpen={true}
          onClose={() => setActiveModal(null)}
          initialData={profile.marketingInfo}
          onSave={handleSaveMarketingInfo}
        />
      )}
    </div>
  );
};

export default CompanyProfilePage;
